import { CurecallInput, CurecallSelect, CurecallSelectItem } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { NAVIGATION_DEFAULT_LOCALE, NAVIGATION_LOCALES, TNavigationLocales } from 'src/types/locales'
import { TNewVariableFormValues } from './new-variable-form.component'
import { useProjectVariables } from '../use-project-variables.hook'
import { getSuggestionsForLanguage } from './variable-suggestions.helper'
import { useDebouncedCallback } from 'use-debounce'
import { TVariable } from 'src/types/variable'
import { VariableSuggestion } from './variable-suggestion.component'

type TLanguageOption = CurecallSelectItem<TNavigationLocales>

interface VariableIntlLabelFieldsProps {
  onVariableSelect?: (variable: TVariable) => void
}

export function VariableIntlLabelFields({ onVariableSelect }: Readonly<VariableIntlLabelFieldsProps>) {
  const { t } = useTranslation()
  const {
    watch,
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext<TNewVariableFormValues>()

  const { data: variables = [] } = useProjectVariables()
  const [selectedLanguage, setSelectedLanguage] = useState<TNavigationLocales>(NAVIGATION_DEFAULT_LOCALE)
  const [activeSuggestions, setActiveSuggestions] = useState<
    Partial<Record<TNavigationLocales, TVariable[]>>
  >({})

  const handleSuggestionClick = (variableId: number) => {
    const selectedVariable = variables.find((v) => v.id === variableId)
    if (selectedVariable && onVariableSelect) {
      onVariableSelect(selectedVariable)
    }
  }

  const updateSuggestions = useDebouncedCallback((language: TNavigationLocales, value: string) => {
    const suggestions = getSuggestionsForLanguage(variables, language, value)
    setActiveSuggestions((prev) => ({ ...prev, [language]: suggestions }))
  }, 300)

  const intlLabel = watch('intlLabel')
  const languagesOptions: TLanguageOption[] = NAVIGATION_LOCALES.map((language: TNavigationLocales) => {
    const isLabelFilledForThisLanguage = (intlLabel as any)?.[language]?.length
    return {
      label: t('common.languages', { language }),
      value: language,
      extraLabel: isLabelFilledForThisLanguage ? undefined : (
        <p className="c-ml-2 c-text-grey-500 c-italic c-m-0">
          {t('pages.configuration.followups.chatbot.variable-modal.no-label')}
        </p>
      ),
    }
  })

  const selectedItem = useMemo(
    () => languagesOptions.find((option) => option.value === selectedLanguage),
    [languagesOptions, selectedLanguage],
  )

  const currentLabelFieldName = `intlLabel.${selectedLanguage}` as `intlLabel.${TNavigationLocales}`

  return (
    <>
      <CurecallSelect
        selectedItem={selectedItem}
        onChange={(option) => setSelectedLanguage(option.value)}
        label={t('pages.configuration.followups.chatbot.variable-modal.variable-label-language')}
        items={languagesOptions}
        wrapperClassName="c-flex-1"
      />

      <Controller
        key={selectedLanguage}
        control={control}
        name={currentLabelFieldName}
        rules={{ required: true }}
        render={({ field }) => (
          <div className="c-flex-2">
            <CurecallInput
              label={t('pages.configuration.followups.chatbot.variable-modal.variable-label')}
              placeholder={t(
                'pages.configuration.followups.chatbot.variable-modal.variable-label-placeholder',
              )}
              tooltipContent={t(
                'pages.configuration.followups.chatbot.variable-modal.variable-label-tooltip',
              )}
              tooltipPosition="top-end"
              value={field.value || ''}
              onChange={(value) => {
                field.onChange(value)
                const inputValue = typeof value === 'string' ? value : value.target.value
                updateSuggestions(selectedLanguage, inputValue)
                if (errors.intlLabel?.[selectedLanguage] && value) {
                  clearErrors(currentLabelFieldName)
                }
              }}
              isRequired={true}
              errorMessage={errors.intlLabel?.[selectedLanguage] && t('common.form.required')}
              wrapperClassName="c-w-full"
            />

            <VariableSuggestion
              suggestions={activeSuggestions[selectedLanguage] || []}
              language={selectedLanguage}
              onSuggestionClick={handleSuggestionClick}
            />
          </div>
        )}
      />
    </>
  )
}
